import { createContext, useContext, useState, useMemo } from 'react';
import { curText } from '@/i18n/tr';
import zh from '@/i18n/zh';
import en from '@/i18n/en';
import es from '@/i18n/es';
import pt from '@/i18n/pt';

const translations = { zh, en, es, pt };

const LangContext = createContext();

// First visit (nothing saved yet): follow the device language — zh / en / pt / es, else the route default.
export const detectBrowserLang = (fallback) => {
  try {
    const langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
    for (const l of langs) {
      const code = String(l || '').toLowerCase().slice(0, 2);
      if (translations[code]) return code;
    }
  } catch { /* no navigator */ }
  return fallback;
};
export const LangProvider = ({ children, storageKey = 'app_lang', defaultLang = 'zh' }) => {
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved && translations[saved]) return saved;
    const detected = detectBrowserLang(defaultLang);
    try { localStorage.setItem(storageKey, detected); } catch { /* private mode */ }
    return detected;
  });
  const [tick, setTick] = useState(0);

  const t = useMemo(() => {
    const current = translations[lang] || translations.zh;
    const fallback = translations.zh;
    return (key) => curText(current[key] || fallback[key] || key);
  }, [lang, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeLang = (newLang) => {
    localStorage.setItem(storageKey, newLang);
    setLang(newLang);
    // Force re-render even when state already equals newLang: localStorage may have
    // drifted (e.g. LandingPage wrote app_lang) and tr() reads localStorage directly.
    setTick((v) => v + 1);
  };

  return (
    <LangContext.Provider value={{ lang, t, changeLang }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);
export default LangContext;
